import type { LangfuseSpanClient } from 'langfuse'

import { getConsultationRootTrace } from './consultation.trace'
import { ANAMNESIS_PIPELINE_SPANS } from './pipeline.spans'
import { buildBrowserLangfuseBaseMetadata } from './langfuse.client'
import { sanitizeLangfuseMetadata } from './telemetry.sanitizer'
import { buildPipelineTimingMetrics, createPerformanceTimer, runTelemetrySafely } from './telemetry.helpers'

type ChunkSpanState = {
  received?: LangfuseSpanClient
  upload?: LangfuseSpanClient
  stt?: LangfuseSpanClient
  diarization?: LangfuseSpanClient
  speaker?: LangfuseSpanClient
  timers: {
    receivedAt?: number
    uploadStartedAt?: number
  }
}

const chunkSpans = new Map<string, ChunkSpanState>()

const chunkKey = (consultationId: string, chunkId: string): string => `${consultationId}:${chunkId}`

const getChunkState = (consultationId: string, chunkId: string): ChunkSpanState => {
  const key = chunkKey(consultationId, chunkId)
  const existing = chunkSpans.get(key)
  if (existing) return existing
  const created: ChunkSpanState = { timers: {} }
  chunkSpans.set(key, created)
  return created
}

const startChildSpan = (
  consultationId: string,
  chunkId: string,
  name: string,
  metadata: Record<string, string | number | boolean | null>,
): LangfuseSpanClient | null => {
  const root = getConsultationRootTrace(consultationId)
  if (!root) return null
  return root.span({
    name,
    metadata: sanitizeLangfuseMetadata(
      buildBrowserLangfuseBaseMetadata({
        consultation_id: consultationId,
        chunk_id: chunkId,
        ...metadata,
      }),
    ),
  })
}

export const recordAudioChunkReceived = (options: {
  consultationId: string
  chunkId: string
  sequence: number
  durationMs?: number
  provider?: string
}): void => {
  runTelemetrySafely(() => {
    const state = getChunkState(options.consultationId, options.chunkId)
    state.timers.receivedAt = performance.now()
    state.received = startChildSpan(options.consultationId, options.chunkId, ANAMNESIS_PIPELINE_SPANS.AUDIO_CHUNK_RECEIVED, {
      sequence: options.sequence,
      ...(options.durationMs != null ? { duration_ms: options.durationMs } : {}),
      ...(options.provider ? { provider: options.provider } : {}),
    }) ?? undefined
  })
}

export const recordAudioUploadStarted = (options: {
  consultationId: string
  chunkId: string
  engineAttempt?: number
}): void => {
  runTelemetrySafely(() => {
    const state = getChunkState(options.consultationId, options.chunkId)
    state.timers.uploadStartedAt = performance.now()
    state.upload = startChildSpan(options.consultationId, options.chunkId, ANAMNESIS_PIPELINE_SPANS.AUDIO_UPLOAD, {
      ...(options.engineAttempt != null ? { engine_attempt: options.engineAttempt } : {}),
    }) ?? undefined
  })
}

export const recordSttProcessing = (options: {
  consultationId: string
  chunkId: string
  latencyMs?: number
  provider?: string
  status?: string
  upstreamOutcome?: string
}): void => {
  runTelemetrySafely(() => {
    const state = getChunkState(options.consultationId, options.chunkId)
    const uploadMs =
      state.timers.uploadStartedAt != null
        ? Math.max(0, Math.round(performance.now() - state.timers.uploadStartedAt))
        : undefined
    state.stt = startChildSpan(options.consultationId, options.chunkId, ANAMNESIS_PIPELINE_SPANS.STT_PROCESSING, {
      ...(options.latencyMs != null ? { stt_ms: options.latencyMs } : {}),
      ...(options.provider ? { provider: options.provider } : {}),
      ...(options.status ? { status: options.status } : {}),
      ...(options.upstreamOutcome ? { upstream_outcome: options.upstreamOutcome } : {}),
      ...buildPipelineTimingMetrics({ uploadMs, sttMs: options.latencyMs }),
    }) ?? undefined
    state.upload?.end({
      metadata: sanitizeLangfuseMetadata(
        buildBrowserLangfuseBaseMetadata({
          upload_ms: uploadMs ?? null,
        }),
      ),
    })
    state.stt?.end({
      metadata: sanitizeLangfuseMetadata(
        buildBrowserLangfuseBaseMetadata({
          ...(options.latencyMs != null ? { stt_ms: options.latencyMs } : {}),
        }),
      ),
    })
  })
}

export const recordDiarizationProcessing = (options: {
  consultationId: string
  chunkId: string
  diarizationMs?: number
  diarizationBackendKind?: string
  failed?: boolean
  reason?: string
}): void => {
  runTelemetrySafely(() => {
    const state = getChunkState(options.consultationId, options.chunkId)
    state.diarization =
      startChildSpan(options.consultationId, options.chunkId, ANAMNESIS_PIPELINE_SPANS.DIARIZATION_PROCESSING, {
        ...(options.diarizationBackendKind ? { diarization_backend_kind: options.diarizationBackendKind } : {}),
        ...(options.failed ? { failed: true } : {}),
        ...(options.reason ? { reason: options.reason } : {}),
        ...(options.diarizationMs != null ? { diarization_ms: options.diarizationMs } : {}),
      }) ?? undefined
    state.diarization?.end()
  })
}

export const recordSpeakerIdentification = (options: {
  consultationId: string
  chunkId: string
  speakerKind?: string
  confidence?: number
  lowConfidence?: boolean
  verificationFailed?: boolean
}): void => {
  runTelemetrySafely(() => {
    const state = getChunkState(options.consultationId, options.chunkId)
    state.speaker =
      startChildSpan(options.consultationId, options.chunkId, ANAMNESIS_PIPELINE_SPANS.SPEAKER_IDENTIFICATION, {
        ...(options.speakerKind ? { speaker_kind: options.speakerKind } : {}),
        ...(options.confidence != null ? { confidence: options.confidence } : {}),
        ...(options.lowConfidence ? { low_confidence: true } : {}),
        ...(options.verificationFailed ? { verification_failed: true } : {}),
      }) ?? undefined
    state.speaker?.end()
    const endToEndMs =
      state.timers.receivedAt != null
        ? Math.max(0, Math.round(performance.now() - state.timers.receivedAt))
        : undefined
    state.received?.end({
      metadata: sanitizeLangfuseMetadata(
        buildBrowserLangfuseBaseMetadata({
          ...buildPipelineTimingMetrics({ endToEndMs }),
        }),
      ),
    })
    chunkSpans.delete(chunkKey(options.consultationId, options.chunkId))
  })
}

export const recordStreamingInterruption = (options: {
  consultationId: string
  reason: string
  provider?: string
}): void => {
  runTelemetrySafely(() => {
    const root = getConsultationRootTrace(options.consultationId)
    root?.event({
      name: 'streaming_interruption',
      metadata: sanitizeLangfuseMetadata(
        buildBrowserLangfuseBaseMetadata({
          reason: options.reason,
          ...(options.provider ? { provider: options.provider } : {}),
        }),
      ),
    })
  })
}

export const withUiDeliverySpan = async <T>(
  consultationId: string,
  metadata: Record<string, string | number | boolean | null>,
  run: () => Promise<T>,
): Promise<T> => {
  const timer = createPerformanceTimer()
  const root = getConsultationRootTrace(consultationId)
  const span = root?.span({
    name: ANAMNESIS_PIPELINE_SPANS.UI_DELIVERY,
    metadata: sanitizeLangfuseMetadata(buildBrowserLangfuseBaseMetadata(metadata)),
  })
  let ended = false
  const endSpan = (level?: 'ERROR', statusMessage?: string) => {
    if (ended) return
    ended = true
    span?.end({
      ...(level ? { level } : {}),
      ...(statusMessage ? { statusMessage } : {}),
      metadata: sanitizeLangfuseMetadata(
        buildBrowserLangfuseBaseMetadata({ ui_delivery_ms: timer.elapsedMs() }),
      ),
    })
  }

  try {
    return await run()
  } catch (error) {
    endSpan('ERROR', error instanceof Error ? error.name : 'error')
    throw error
  } finally {
    endSpan()
  }
}
